/**
 * Shareable Card - Display and share highlight moments
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  ShareableStats,
  shareToTwitter,
  downloadShareableImage,
  generateShareText,
} from '../../services/shareService';
import { getReferralStats } from '../../api/referrals';
import { useWallet } from '@solana/wallet-adapter-react';
import { XTwitterIcon } from './icons';

interface ShareableCardProps {
  stats: ShareableStats;
  onClose: () => void;
}

export const ShareableCard: React.FC<ShareableCardProps> = ({ stats, onClose }) => {
  const { publicKey } = useWallet();
  const [downloading, setDownloading] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);

  // Fetch referral code on mount
  React.useEffect(() => {
    if (publicKey) {
      getReferralStats(publicKey.toBase58())
        .then((s) => setReferralCode(s.referralCode))
        .catch(() => {});
    }
  }, [publicKey]);

  const handleShare = () => {
    const shareUrl = referralCode
      ? `${window.location.origin}?ref=${referralCode}`
      : window.location.origin;
    const text = generateShareText(stats);
    shareToTwitter(text, shareUrl);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadShareableImage({ ...stats, referralCode: referralCode ?? undefined });
      toast.success('Image downloaded!');
    } catch {
      toast.error('Failed to download image');
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyLink = () => {
    const shareUrl = referralCode
      ? `${window.location.origin}?ref=${referralCode}`
      : window.location.origin;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied!');
  };

  const typeStyles: Record<ShareableStats['type'], { bg: string; border: string; accent: string }> =
    {
      victory: {
        bg: 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20',
        border: 'border-cyan-400/40',
        accent: 'text-cyan-400',
      },
      high_score: {
        bg: 'bg-gradient-to-br from-amber-500/20 to-yellow-500/20',
        border: 'border-amber-400/40',
        accent: 'text-amber-400',
      },
      comeback: {
        bg: 'bg-gradient-to-br from-rose-500/20 to-red-500/20',
        border: 'border-rose-400/40',
        accent: 'text-rose-400',
      },
      streak: {
        bg: 'bg-gradient-to-br from-purple-500/20 to-pink-500/20',
        border: 'border-purple-400/40',
        accent: 'text-purple-400',
      },
    };

  const typeLabels: Record<ShareableStats['type'], string> = {
    victory: 'VICTORY!',
    high_score: 'EPIC PLAY!',
    comeback: 'COMEBACK!',
    streak: `${stats.winStreak} WIN STREAK!`,
  };

  const style = typeStyles[stats.type];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className={`w-full max-w-md rounded-2xl border ${style.border} ${style.bg} p-4 sm:p-6 shadow-2xl`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-white/50 hover:text-white"
          >
            &times;
          </button>

          {/* Header */}
          <div className="text-center mb-6">
            <motion.h2
              initial={{ scale: 1.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className={`font-display text-2xl sm:text-3xl font-bold ${style.accent}`}
            >
              {typeLabels[stats.type]}
            </motion.h2>
            <p className="text-white/50 text-sm mt-1">Share your achievement!</p>
          </div>

          {/* Stats Display */}
          <div className="arcade-panel p-4 mb-6">
            <div className="text-center">
              <p className="text-4xl sm:text-6xl font-display font-bold text-white mb-2">
                {stats.score}
              </p>
              {stats.opponentScore !== undefined && (
                <p className="text-white/50 text-sm">vs {stats.opponentScore}</p>
              )}
              {stats.handType && (
                <p className={`text-sm font-semibold ${style.accent} mt-2`}>{stats.handType}</p>
              )}
            </div>
          </div>

          {/* Share Buttons */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={handleShare}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-500 px-4 py-3 font-semibold text-white hover:bg-blue-600 transition"
            >
              <XTwitterIcon className="w-5 h-5" />
              Share on X
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-white/10 px-4 py-3 font-semibold text-white hover:bg-white/20 transition disabled:opacity-50"
              >
                {downloading ? 'Generating...' : 'Download Image'}
              </button>
              <button
                type="button"
                onClick={handleCopyLink}
                className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-white/10 px-4 py-3 font-semibold text-white hover:bg-white/20 transition"
              >
                Copy Link
              </button>
            </div>
          </div>

          {/* Referral hint */}
          {referralCode && (
            <p className="text-center text-xs text-white/40 mt-4">
              Your referral code ({referralCode}) will be included!
            </p>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Hook to detect and trigger shareable moments
 */
export function useShareableMoment() {
  const [shareableStats, setShareableStats] = useState<ShareableStats | null>(null);

  const triggerShare = (stats: ShareableStats) => {
    setShareableStats(stats);
  };

  const closeShare = () => {
    setShareableStats(null);
  };

  return {
    shareableStats,
    triggerShare,
    closeShare,
    ShareComponent: shareableStats ? (
      <ShareableCard stats={shareableStats} onClose={closeShare} />
    ) : null,
  };
}
